// The kept copy of each ready proposal's diff, read once its card shows. A
// copy the server is still asking its runner for is read again shortly; a
// head never changes, so a copy read is kept for as long as the board shows.
import { useEffect, useRef, useState } from 'react'
import { ApiError, errorMessage } from '../api/client'
import type { SessionChanges } from '../api/types'
import { useSessionsEnv } from '../sessions/context'
import { useBoardState, useStore } from '../store/context'

/** How long before a copy being prepared is read again. */
export const READ_AGAIN_MS = 3_000

/** What the board knows of a proposal's copy: its changes, or that it is being prepared, or why it could not be read. */
export interface CopyState {
  changes: SessionChanges | null
  /** The server is asking the proposing session's runner for it. */
  preparing: boolean
  /** The server's last failure to make it, or the read's own; null otherwise. */
  problem: string | null
}

export function useProposalCopies(ids: readonly string[]): ReadonlyMap<string, CopyState> {
  const { api } = useSessionsEnv()
  const store = useStore()
  const { me } = useBoardState()
  const as = me?.member?.id ?? null
  const [copies, setCopies] = useState<ReadonlyMap<string, CopyState>>(() => new Map())
  const latest = useRef(copies)
  latest.current = copies
  const key = ids.join('\n')
  useEffect(() => {
    let live = true
    const timers = new Set<ReturnType<typeof setTimeout>>()
    const set = (id: string, state: CopyState) => setCopies((all) => new Map(all).set(id, state))
    const read = (id: string) => api.proposalChanges(id, as).then(
      (answer) => {
        if (!live) return
        const preparing = answer.changes === null && answer.asked !== null
        set(id, { changes: answer.changes, preparing, problem: answer.changes === null && !preparing ? (answer.failure ?? 'there is no copy of it yet') : null })
        if (!preparing) return
        const timer = setTimeout(() => { timers.delete(timer); void read(id) }, READ_AGAIN_MS)
        timers.add(timer)
      },
      (err: unknown) => {
        if (!live) return
        set(id, { changes: null, preparing: false, problem: errorMessage(err) })
        if (err instanceof ApiError && err.status === 401) void store.whenSessionKnown()
      },
    )
    for (const id of key.split('\n')) if (id && !latest.current.get(id)?.changes) void read(id)
    return () => {
      live = false
      for (const timer of timers) clearTimeout(timer)
    }
  }, [key, api, as, store])
  return copies
}
